import { useMemo, useState } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@predoc/cow';
import { DataTable, TableSkeleton } from '@/components';
import { useGetPatientMedicalDataAllMedicationData } from '@/feature/patients/hooks/medicalDataAPI';
import { MedicationAllDataRecord } from '../types';
import { createFullMedicationsColumns } from '../columns';

interface ViewAllDataButtonProps {
  patientId?: string;
}

export const ViewAllDataButton = ({ patientId }: ViewAllDataButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data, isLoading } = useGetPatientMedicalDataAllMedicationData(
    isOpen && patientId
      ? {
          patientId,
          pagination: { offset: 0, limit: 100 },
        }
      : undefined,
  );

  const columns = useMemo(() => createFullMedicationsColumns(), []);
  const records = (data?.records ?? []) as MedicationAllDataRecord[];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-muted-foreground h-6 text-sm"
          aria-label="View All Data"
        >
          View All Data
        </Button>
      </DialogTrigger>
      <DialogContent
        className="flex max-h-[85vh] w-[90vw] flex-col p-0 sm:max-w-7xl"
        aria-describedby={undefined}
      >
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>All Medications Data</DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto p-0 pb-1">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={5} columns={8} />
            </div>
          ) : (
            <DataTable
              columns={columns as ColumnDef<MedicationAllDataRecord>[]}
              data={records}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
